import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MobileShell from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { ShieldCheck, PlayCircle, Stethoscope, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1764314484083-cbd0de7e512c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXB5JTIwcGF0aWVudCUyMGNsaW5pY3xlbnwwfHx8fDE3ODE3Mzc3ODJ8MA&ixlib=rb-4.1.0&q=85";

export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <MobileShell>
      <div className="px-6 pt-10 pb-6">
        <div className="flex items-center gap-2 text-teal-700">
          <Stethoscope size={22} />
          <span className="text-xs font-bold uppercase tracking-[0.2em]">Bexley Road Physiotherapy</span>
        </div>
        <h1 className="mt-4 text-4xl font-bold text-slate-900 tracking-tight font-display">
          Your guided recovery, in your pocket.
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          A structured rehab pathway designed by our clinicians. Track pain, follow daily sessions, and build confidence
          week by week.
        </p>
      </div>

      <div className="px-6">
        <div className="relative rounded-3xl overflow-hidden shadow-sm border border-slate-100">
          <img src={HERO} alt="Physiotherapist with patient" className="w-full h-56 object-cover" />
          <button
            type="button"
            data-testid="welcome-video-placeholder"
            className="absolute inset-0 flex items-center justify-center bg-slate-900/20 hover:bg-slate-900/30 transition-colors"
          >
            <span className="flex items-center gap-2 bg-white text-slate-900 rounded-full px-4 py-2 font-semibold shadow">
              <PlayCircle size={18} /> Watch welcome (coming soon)
            </span>
          </button>
        </div>
      </div>

      <div className="px-6 mt-8">
        <h2 className="text-xl font-semibold font-display text-slate-900">How the app works</h2>
        <ol className="mt-4 space-y-3">
          {[
            ["Pick your condition", "Choose from 13 evidence-based pathways."],
            ["Quick safety check", "Short intake that screens for red flags."],
            ["Follow your weekly plan", "15–20 minute daily sessions, with education and tracking."],
            ["Step up to the clinic", "Book a review or class when you're ready."],
          ].map(([t, d], i) => (
            <li key={t} className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-teal-50 text-teal-700 font-semibold grid place-items-center text-sm">
                {i + 1}
              </span>
              <div>
                <div className="font-semibold text-slate-900">{t}</div>
                <div className="text-sm text-slate-600">{d}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="px-6 mt-8">
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3">
          <ShieldCheck size={18} className="shrink-0 mt-0.5" />
          <p>
            <strong>Safety note:</strong> This app supports your rehab and does not replace individual medical advice.
            Seek urgent care for severe weakness, loss of bowel/bladder control, or other red flags.
          </p>
        </div>
      </div>

      <div className="px-6 mt-8 space-y-3">
        {user ? (
          <Button
            data-testid="welcome-continue-btn"
            onClick={() => navigate("/dashboard")}
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base"
          >
            Continue <ArrowRight size={18} className="ml-2" />
          </Button>
        ) : (
          <>
            <Button
              data-testid="welcome-start-btn"
              onClick={() => navigate("/auth?mode=register")}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base"
            >
              Start now <ArrowRight size={18} className="ml-2" />
            </Button>
            <Link
              to="/auth?mode=login"
              data-testid="welcome-login-link"
              className="block text-center text-sm text-slate-500 hover:text-teal-600"
            >
              I already have an account
            </Link>
          </>
        )}
      </div>
    </MobileShell>
  );
}
